import React, { useEffect, useState } from "react";
import { Fuel, Zap, Flame, Plus, X, MapPin, Phone, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// FuelStations — Oil / Charging / Gas station finder with distance + prices.
// Sits on top of the /api/fuel-stations backend. Users can add stations too.
// -----------------------------------------------------------------------------
const KIND_META = {
  oil_pump:          { Icon: Fuel,  label: "Oil pump",         accent: "text-amber-600" },
  charging_station:  { Icon: Zap,   label: "EV charging",      accent: "text-emerald-600" },
  gas_pump:          { Icon: Flame, label: "Gas pump (CNG/LPG)", accent: "text-orange-600" },
  multi:             { Icon: Fuel,  label: "Multi-fuel",       accent: "text-stone-600" },
};

export default function FuelStations() {
  const { user } = useAuth();
  const { loc, detect } = useUserLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [fuel, setFuel] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  useSeo({
    title: "Oil / EV / Gas stations near you · find2near",
    description: "Nearby petrol pumps, EV chargers and CNG/LPG stations with live prices, connector types and 24×7 flags.",
    canonical: "/fuel",
  });

  const load = () => {
    setLoading(true);
    const params = { limit: 60 };
    if (kind) params.type = kind;
    if (fuel) params.fuel = fuel;
    if (q)    params.q = q;
    if (loc.lat && loc.lng) { params.lat = loc.lat; params.lng = loc.lng; params.radius_km = 25; }
    api.get("/fuel-stations", { params })
       .then((r) => setItems(r.data))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [kind, fuel, loc.lat, loc.lng]); // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="fuel-page">
      <FuelForm open={showForm} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Fuel size={12} className="text-amber-600" /> Fuel · charging · gas</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every pump. <span className="text-amber-600">One tap.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Petrol · Diesel · CNG · LPG · EV chargers · Hydrogen. Real prices from the community.</p>
        </div>
        {user && (
          <Button onClick={() => setShowForm(true)} className="rounded-full bg-amber-600 hover:bg-amber-700 font-black" data-testid="fuel-new-btn">
            <Plus size={14} className="mr-1.5" /> Add station
          </Button>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search brand / area…" className="rounded-full h-9 flex-1 min-w-[180px] text-sm" data-testid="fuel-q" />
        <Select value={kind || "__all__"} onValueChange={(v) => setKind(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-40 text-xs" data-testid="fuel-kind"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {Object.entries(KIND_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fuel || "__any__"} onValueChange={(v) => setFuel(v === "__any__" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-36 text-xs" data-testid="fuel-fuel"><SelectValue placeholder="Any fuel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__any__">Any fuel</SelectItem>
            {["petrol", "diesel", "cng", "lpg", "ev", "hydrogen"].map((f) => <SelectItem key={f} value={f}>{f.toUpperCase()}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={() => detect()} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-full px-3 h-9" data-testid="fuel-locate">
          <MapPin size={12} className="inline mr-1" /> Use my location
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No stations yet in this filter.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => {
            const meta = KIND_META[s.kind] || KIND_META.multi;
            const Icon = meta.Icon;
            return (
              <div key={s.station_id} className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`fuel-card-${s.station_id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-2xl bg-stone-50 grid place-items-center ${meta.accent}`}><Icon size={20} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-stone-900 truncate">{s.name}</div>
                    <div className="text-[11px] text-stone-500 flex items-center gap-1"><Clock size={10} /> {s.open_24x7 ? "24 × 7" : "See board"} {s.distance_km != null && <> · <MapPin size={10} /> {s.distance_km} km</>}</div>
                    {s.brand && <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">{s.brand}</div>}
                  </div>
                </div>
                {s.fuels?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.fuels.map((f) => <span key={f} className="text-[10px] font-black bg-stone-100 rounded-full px-2 py-0.5 uppercase">{f}</span>)}
                  </div>
                )}
                {s.prices && Object.keys(s.prices).length > 0 && (
                  <div className="mt-2 text-xs text-stone-600 font-mono grid grid-cols-2 gap-x-2 gap-y-0.5">
                    {Object.entries(s.prices).slice(0, 4).map(([k, v]) => (
                      <div key={k} className="flex justify-between"><span className="uppercase text-stone-500">{k}</span><span className="font-bold text-stone-900">₹{Number(v).toFixed(2)}</span></div>
                    ))}
                  </div>
                )}
                {s.ev_kw && <div className="mt-2 text-xs text-emerald-700 font-bold">⚡ {s.ev_kw} kW · {s.ev_connectors?.join(", ") || "connectors n/a"}</div>}
                {s.address && <div className="mt-2 text-xs text-stone-600 line-clamp-2">{s.address}</div>}
                {s.phone && <a href={`tel:${s.phone}`} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700"><Phone size={11} /> {s.phone}</a>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const emptyStation = () => ({
  name: "", kind: "oil_pump",
  fuels: [], prices: {},
  ev_kw: "", ev_connectors: "",
  address: "", city: "", lat: "", lng: "",
  open_24x7: false, phone: "", brand: "",
});

function FuelForm({ open, onClose, onSaved }) {
  const [f, setF] = useState(emptyStation());
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const submit = async () => {
    if (f.name.trim().length < 2) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f,
        lat: f.lat === "" ? null : parseFloat(f.lat),
        lng: f.lng === "" ? null : parseFloat(f.lng),
        ev_kw: f.ev_kw === "" ? null : parseFloat(f.ev_kw),
        ev_connectors: (typeof f.ev_connectors === "string" ? f.ev_connectors.split(",") : f.ev_connectors).map(x => x.trim()).filter(Boolean),
      };
      await api.post("/fuel-stations", payload);
      toast.success("Station added");
      setF(emptyStation());
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggleFuel = (f2) => set({ fuels: f.fuels.includes(f2) ? f.fuels.filter(x => x !== f2) : [...f.fuels, f2] });
  const setPrice = (k, v) => set({ prices: { ...f.prices, [k]: v ? parseFloat(v) : undefined } });

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fuel-form-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-white">
          <div className="flex items-center gap-2"><Fuel className="text-amber-700" size={16} /><b>Add fuel / charging / gas station</b></div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Station name" className="rounded-2xl h-10" data-testid="fuel-form-name" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.kind} onValueChange={(v) => set({ kind: v })}>
              <SelectTrigger className="rounded-2xl h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KIND_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.brand} onChange={(e) => set({ brand: e.target.value })} placeholder="Brand (IOC, HPCL, Tata Power…)" className="rounded-2xl h-10" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Fuels available</div>
            <div className="flex flex-wrap gap-1">
              {["petrol", "diesel", "cng", "lpg", "ev", "hydrogen"].map((ff) => (
                <button key={ff} onClick={() => toggleFuel(ff)} className={`text-[11px] font-black rounded-full px-3 py-1 border ${f.fuels.includes(ff) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-stone-700 border-stone-200"}`}>{ff.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["petrol", "diesel", "cng", "lpg"].map((k) => (
              <Input key={k} type="number" step="0.01" placeholder={`${k.toUpperCase()} ₹/L`} value={f.prices?.[k] ?? ""} onChange={(e) => setPrice(k, e.target.value)} className="rounded-2xl h-10 font-mono" />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" step="0.1" value={f.ev_kw} onChange={(e) => set({ ev_kw: e.target.value })} placeholder="EV peak kW" className="rounded-2xl h-10 font-mono" />
            <Input value={f.ev_connectors} onChange={(e) => set({ ev_connectors: e.target.value })} placeholder="Connectors (CCS2, Type2, CHAdeMO)" className="rounded-2xl h-10 font-mono" />
          </div>
          <Input value={f.address} onChange={(e) => set({ address: e.target.value })} placeholder="Address" className="rounded-2xl h-10" />
          <div className="grid grid-cols-4 gap-2">
            <Input value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="City" className="rounded-2xl h-10 col-span-2" />
            <Input value={f.lat} onChange={(e) => set({ lat: e.target.value })} placeholder="Lat" className="rounded-2xl h-10 font-mono" />
            <Input value={f.lng} onChange={(e) => set({ lng: e.target.value })} placeholder="Lng" className="rounded-2xl h-10 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2 items-center">
            <Input value={f.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="Phone" className="rounded-2xl h-10 font-mono" />
            <label className="text-sm text-stone-700 inline-flex items-center gap-2"><input type="checkbox" checked={f.open_24x7} onChange={(e) => set({ open_24x7: e.target.checked })} /> Open 24 × 7</label>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full mt-2 rounded-full bg-amber-600 hover:bg-amber-700 font-black" data-testid="fuel-form-submit">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null} Add station
          </Button>
        </div>
      </div>
    </div>
  );
}
